import { useState, useEffect } from "react";
import searchIcon from "../../assets/icons/search.svg";
import { PanelHeader } from "../../components/PanelHeader";
import { RequestCard } from "../../components/RequestCard";
import { useNavigate } from "react-router-dom";

import {getHelpCases,getLoggedUser,getHelpCaseFileUrl,HelpCase} 
from "../../service/helpCaseService";

import {
  Container,
  Content,
  TabsContainer,
  TabsWrapper,
  Tab,
  Badge,
  SearchContainer,
  SearchInput,
  SearchIcon,
  RequestsList,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  ModalButton,
} from "./styles";

interface Request {
  id: string;
  email: string;
  date: string;
  scenarioName?: string;
  content?: string;
  status: "pendente" | "respondida";
  videoUrl?: string;
  originalHelpCase?: HelpCase;
  answers: HelpCase[];
}

export function InterpreterPanel() {
  const [activeTab, setActiveTab] = useState("todas");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [loggedUserId, setLoggedUserId] = useState<number | null>(null);
  const [requests, setRequests] = useState<Request[]>([]);

  const navigate = useNavigate();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const mapStatusByAnswers = (answers: any[]): "pendente" | "respondida" => {
    return Array.isArray(answers) && answers.length > 0 ? "respondida" : "pendente";
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await getLoggedUser();
        setLoggedUserId(userData.id);
      } catch (error) {
        navigate("/login");
      }
    };

    fetchUserData();
  }, []);

  const fetchHelpCases = async () => {
    try {
      setLoading(true);
      const response = await getHelpCases(1, 100);

      if (Array.isArray(response.items) && response.items.length > 0) {
        const mappedRequestsPromises = response.items.map(async (helpCase) => {
          let videoUrl = "";

          if ((helpCase as any).has_video === true) {
            try {
              videoUrl = await getHelpCaseFileUrl(helpCase.id);
            } catch (error: any) {}
          }

          return {
            id: String(helpCase.id),
            email: (helpCase as any).requester_email,
            date: formatDate(helpCase.created_at),
            scenarioName: (helpCase as any).scenario?.name,
            content: (helpCase as any).content,
            status: mapStatusByAnswers((helpCase as any).answers),
            videoUrl: videoUrl,
            answers: helpCase.answers,
            originalHelpCase: helpCase,
          };
        });

        const mappedRequests = await Promise.all(mappedRequestsPromises);
        setRequests(mappedRequests);
      } else {
        setRequests([]);
      }
    } catch (error) {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHelpCases();
  }, []);

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };


  const filteredRequests = requests.filter((req) => {
    const matchesTab =
      activeTab === "todas" ||
      (activeTab === "pendentes" && req.status === "pendente") ||
      (activeTab === "respondidas" && req.status === "respondida");

    const matchesSearch =
      req.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      req.scenarioName?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesTab && matchesSearch;
  });

  const counts = {
    todas: requests.length,
    pendentes: requests.filter((r) => r.status === "pendente").length,
    respondidas: requests.filter((r) => r.status === "respondida").length,
  };

  return (
    <Container>
      <PanelHeader title="Painel do intérprete" />

      <TabsContainer>
        <TabsWrapper>
          <Tab isActive={activeTab === "todas"} onClick={() => handleTabClick("todas")}>
            Todas
            <Badge isActive={activeTab === "todas"}>{counts.todas}</Badge>
          </Tab>
          <Tab isActive={activeTab === "pendentes"} onClick={() => handleTabClick("pendentes")}>
            Pendentes
            <Badge isActive={activeTab === "pendentes"}>{counts.pendentes}</Badge>
          </Tab>
          <Tab isActive={activeTab === "respondidas"} onClick={() => handleTabClick("respondidas")}>
            Respondidas
            <Badge isActive={activeTab === "respondidas"}>{counts.respondidas}</Badge>
          </Tab>
        </TabsWrapper>

        <SearchContainer>
          <SearchIcon><img src={searchIcon} alt="Buscar" /></SearchIcon>
          <SearchInput type="text" placeholder="Buscar" value={searchQuery} onChange={handleSearchChange} />
        </SearchContainer>
      </TabsContainer>

      <Content>
        <RequestsList>
          {loading ? (
            <div style={{ color: 'white', textAlign: 'center', padding: '2rem' }}>
              Carregando solicitações...
            </div>
          ) : filteredRequests.length === 0 ? (
            <div style={{ color: 'white', textAlign: 'center', padding: '2rem' }}>
              Nenhuma solicitação encontrada
            </div>
          ) : (
            filteredRequests.map((request) => (
              <RequestCard
                key={request.id}
                id={request.id}
                email={request.email}
                date={request.date}
                scenarioName={request.scenarioName}
                content={request.content}
                status={request.status}
                videoUrl={request.videoUrl}
                onReplySuccess={fetchHelpCases}
                answers={request.answers}
              />
            ))
          )}
        </RequestsList>
      </Content>
    </Container>
  );
}
